import React, { useEffect, useRef, useState } from 'react'
import { animated, useSpring } from '@react-spring/web'

import { useSpeechStore } from '../Contexts/SpeechContext'
import { useViewStore } from '../Contexts/ViewContext'
import { useRetorikStore } from '../Contexts/RetorikContext'

import { AnimationSize } from '../../models/enums'

import AnimationContainer from '../Loader/AnimationContainer'
import MicrophoneAndSwitch from '../Menus/Large/MicrophoneAndSwitch'

const springDuration = 1050

interface VoiceInputProps {
  className?: string
}

// Unicode Spaces found here : https://www.fileformat.info/info/unicode/category/Zs/list.htm
const spacingCharacters = ['\u0020', '\u00A0', '\u1680', '\u2000', '\u2001', '\u2002', '\u2003', '\u2004', '\u2005', '\u2006', '\u2007', '\u2008', '\u2009', '\u200A', '\u202F', '\u205F', '\u3000']

const VoiceInput = ({ className }: VoiceInputProps): JSX.Element => {
  const isMobile = useViewStore((state) => state.isMobile)
  const isLandscape = useViewStore((state) => state.isLandscape)
  const lastRecognitionInterim = useSpeechStore((state) => state.lastRecognitionInterim)
  const isWaitingForResponse = useRetorikStore((state) => state.isWaitingForResponse)
  const [remainingLastRecognitionInterim, setRemainingLastRecognitionInterim] = useState<string | null>(null)
  const animationTimerRef = useRef<NodeJS.Timeout>()
  const divRef = useRef<HTMLDivElement>(null)
  const lastSpaceRef = useRef<number>(0)

  const [spring, api] = useSpring(() => ({
    from: {
      opacity: 1
    }
  }))

  useEffect(() => {
    return () => {
      animationTimerRef?.current && clearTimeout(animationTimerRef.current)
      lastSpaceRef.current = 0
    }
  }, [])

  useEffect(() => {
    if (lastRecognitionInterim) {
      if (isLandscape && divRef?.current) {
        if (divRef.current.scrollHeight > divRef.current.clientHeight) {
          let lastSpace = 0

          for (let i = lastRecognitionInterim.length - 1; i > 0; i--) {
            if (spacingCharacters.includes(lastRecognitionInterim[i])) {
              switch (lastSpace) {
                case 0:
                  // First space found, let's wait for another one
                  lastSpace = 1
                  break
                case 1:
                  // Second space found, the next one will be the good one
                  lastSpace = 2
                  break
                case 2:
                  // Take this space as reference to display text
                  lastSpace = 3
                  lastSpaceRef.current = i
                  break
              }

              if (lastSpace === 3) break
            }
          }
        }

        setRemainingLastRecognitionInterim(`${lastSpaceRef.current ? '... ' : ''}${lastRecognitionInterim.substring(lastSpaceRef.current)}`)
      } else {
        setRemainingLastRecognitionInterim(lastRecognitionInterim)
      }
    } else if (remainingLastRecognitionInterim) {
      api.start({
        from: {
          opacity: 1
        },
        to: {
          opacity: 0
        },
        config: {
          duration: springDuration
        }
      })

      animationTimerRef?.current && clearTimeout(animationTimerRef.current)
      animationTimerRef.current = setTimeout(() => {
        api.set({ opacity: 1 })
        setRemainingLastRecognitionInterim(null)
        lastSpaceRef.current = 0
      }, springDuration)
    }
  }, [lastRecognitionInterim])

  return (
    <React.Fragment>
      {isMobile ? (
        isWaitingForResponse ? (
          <AnimationContainer classname='rf-flex rf-justify-center rf-py-3 rf-items-center' subClassname='rf-w-4 rf-h-4' size={AnimationSize.medium} useTextColor={true} />
        ) : remainingLastRecognitionInterim ? (
          <animated.p className={className} style={{ ...spring }}>
            {remainingLastRecognitionInterim}
          </animated.p>
        ) : (
          <React.Fragment />
        )
      ) : (
        <div className='rf-w-full rf-h-full rf-flex rf-flex-col rf-justify-end landscape-retorik:rf-justify-start rf-gap-2'>
          {isLandscape ? (
            <React.Fragment>
              <MicrophoneAndSwitch />
              {remainingLastRecognitionInterim && (
                <animated.div
                  ref={divRef}
                  className='rf-w-full rf-max-h-16 rf-pt-2 rf-subtitle-size-auto rf-font-bold rf-text-truewhite rf-text-center rf-text-shadow-black rf-overflow-hidden'
                  style={{ ...spring }}
                >
                  {remainingLastRecognitionInterim}
                </animated.div>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {remainingLastRecognitionInterim && (
                <animated.div className='rf-w-full rf-py-3 rf-subtitle-size-auto rf-font-bold rf-text-truewhite rf-text-center rf-text-shadow-black' style={{ ...spring }}>
                  {remainingLastRecognitionInterim}
                </animated.div>
              )}
            </React.Fragment>
          )}
        </div>
      )}
    </React.Fragment>
  )
}

export default VoiceInput
